import type { ApolloQueryResult } from 'apollo-client';
import type { Ari } from '@atlassian/jira-platform-ari/src/index.tsx';
import { createEarlyFetchCache } from '@atlassian/jira-polaris-lib-early-fetch-cache/src/index.tsx';
import { getProjectAri } from '@atlassian/jira-polaris-lib-early-fetch-cache/src/utils/ari-generators/index.tsx';
import { fetchData } from '@atlassian/jira-polaris-lib-early-fetch-cache/src/utils/fetch/index.tsx';

import type { GetPolarisProjectQuery } from '../types.tsx';
import { PROJECT_CONFIG_QUERY_STRING } from './constants.tsx';

// Define a type for the Apollo query parameters
interface ProjectConfigQueryParams {
	ari: Ari;
	skipIdeaTypes: boolean;
	isShareViewDialogEnabled: boolean;
	skipDescription: boolean;
}

// Create a type-specific early fetch cache for Project Config
const earlyFetchCache = createEarlyFetchCache<GetPolarisProjectQuery, ProjectConfigQueryParams>({});

/**
 * Gets the cached response and marks it as accessed
 * The cache will be automatically cleared after access or if stale
 */
export const getEarlyFetchResponse = (
	params: ProjectConfigQueryParams,
): Promise<GetPolarisProjectQuery> | null => {
	return earlyFetchCache.getCachedResponse(params);
};

/**
 * Prefetches the project config data and stores it in the cache
 */
export const prefetchProjectConfig = (projectAri: Ari): Promise<GetPolarisProjectQuery> => {
	// Create fetch parameters for the project config query
	const queryParams: ProjectConfigQueryParams = {
		ari: projectAri,
		skipIdeaTypes: true,
		isShareViewDialogEnabled: true,
		skipDescription: true,
	};

	// Create the fetch promise without awaiting it
	const fetchPromise = fetchData<ApolloQueryResult<GetPolarisProjectQuery>>({
		url: '/gateway/api/graphql?operation=jira_polaris_ProjectQuery_reduced',
		method: 'POST',
		body: JSON.stringify({
			operationName: 'jira_polaris_ProjectQuery_reduced',
			query: PROJECT_CONFIG_QUERY_STRING.replace(/\n/g, ' ').trim(),
			variables: queryParams,
		}),
		headers: {
			'X-ExperimentalApi': ['polaris-v0', 'Townsquare'],
		},
	});

	// Transform the promise to handle errors and extract data
	const dataPromise = fetchPromise.then((result: ApolloQueryResult<GetPolarisProjectQuery>) => {
		if (result.errors) {
			throw new Error(`GraphQL errors: ${JSON.stringify(result.errors)}`);
		}
		return result.data;
	});

	// Immediately store the promise in the cache
	earlyFetchCache.setCacheResponse(dataPromise, queryParams);

	return dataPromise;
};

/**
 * Starts the early API request for project config.
 * This function is executed before the main app loads.
 */
export function runProjectConfigEarlyScript(
	projectId: string,
): Promise<GetPolarisProjectQuery> | null {
	// Get the project ARI
	const projectAri = getProjectAri(projectId);
	if (!projectAri) {
		return null;
	}

	// Prefetch the project config data
	return prefetchProjectConfig(projectAri);
}
